import {DialogTitle, IconButton, SvgIconProps, Typography} from '@mui/material';
import Dialog, {DialogProps} from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import React, {forwardRef, ReactElement, useEffect, useId} from 'react';
import {ReactComponent as CloseIcon} from '../../../assests/svg/close_icon.svg';

import {POPUP_BUTTON_STYLE} from '../../../constants/constants';
import AurButton from '../aur-button/AurButton';
import {t} from 'i18next';

interface IPopupDialogProps extends DialogProps {
  labelActionOk?: string;
  labelActionCancel?: string;
  showCloseIcon?: boolean;
  showBackIcon?: boolean;
  popupBackIcon?: React.ReactElement<SvgIconProps>;
  popupTitle?: string;
  popupHeading?: string;
  popupTimer?: string;
  popupContent?: string | React.ReactElement;
  onOk: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onClose?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  buttonStyle?: string;
  ariaLabel?: string;
  ariaDescription?: string;
  showPopupHeading?: boolean;
  showPopupTitle?: boolean;
  exitButtonHint?: string;
  exitButtonAriaHintLabel?: string;
  cancelButtonTitle?: string;
  continueButtonHint?: string;
  continueButtonAriaHintLabel?: string;
  closeIconHint?: string;
  dataTestId?: string;
  headingIcon?: React.ReactElement;
  popupContentClass?: string;
  contentType?: string;
  continueButtonState?: boolean;
  showCancelOption?: boolean;
  showOkOption?: boolean;
  popUpHintText?: string;
  titleCustomClass?: string;
  closeIcon?: ReactElement;
  popupContentParagraph?: string;
  popupContentParagraphCheck?: boolean;
  actionOkLoader?: boolean;
  showActionOkWithLoader?: boolean; // Boolean for display ok label and loader both
  actionOkLoaderClass?: string;
  actionCancelDisable?: boolean;
}

const AurPopup = forwardRef<HTMLButtonElement, IPopupDialogProps>(
  (props, ref) => {
    const {
      popupHeading,
      popupTitle,
      popupTimer,
      popupContent,
      popupContentParagraphCheck = false,
      popupContentParagraph,
      labelActionOk,
      labelActionCancel,
      onCancel,
      onOk,
      className = '',
      showCloseIcon = false,
      showPopupHeading = false,
      showPopupTitle = false,
      buttonStyle = POPUP_BUTTON_STYLE.SIDEBYSIDE,
      exitButtonHint,
      exitButtonAriaHintLabel,
      continueButtonHint,
      continueButtonAriaHintLabel,
      closeIconHint,
      headingIcon,
      popupContentClass,
      contentType = 'text',
      continueButtonState = false,
      showCancelOption = true,
      showOkOption = true,
      cancelButtonTitle = t('common.stayLoggedInMsg'),
      popUpHintText,
      open,
      titleCustomClass,
      closeIcon,
      actionOkLoader,
      showActionOkWithLoader,
      actionOkLoaderClass,
      actionCancelDisable = false,
      ...dialogProps
    } = props;

    const popUpTitleId = useId();
    const popUpHeadingId = useId();
    const contentId = useId();

    useEffect(() => {
      let observer: MutationObserver;

      if (open) {
        observer = new MutationObserver(() => {
          const container = document.querySelector('.MuiDialog-container');
          if (container) {
            container.setAttribute('role', 'dialog');
            observer.disconnect();
          }
        });

        observer.observe(document.body, {
          childList: true, // Look for changes in the child elements of the body
          subtree: true, // Traverse the entire subtree for changes
        });
      }

      return () => {
        if (observer) observer.disconnect(); // Clean up the observer on component unmount or when the dialog closes
      };
    }, [open]);
    return (
      <>
        <span id={contentId} style={{display: 'none'}}>
          {popUpHintText}
        </span>
        <Dialog
          open={open}
          className={`popup ${className}`}
          data-testid="popup-dialog"
          PaperProps={{
            role: 'none',
            'aria-labelledby': showPopupTitle ? popUpTitleId : popUpHeadingId,
            'aria-describedby': contentId,
          }}
          {...dialogProps}
        >
          <DialogTitle>
            {(showPopupTitle || showCloseIcon) && (
              <div className="popup_title-container">
                {showPopupTitle ? (
                  <div className="popup_title-text">
                    <Typography
                      className={`typo-text label1_size ${titleCustomClass}`}
                      component="div"
                      id={popUpTitleId}
                    >
                      {popupTitle}{' '}
                    </Typography>
                  </div>
                ) : null}
                {showCloseIcon ? (
                  <IconButton
                    onClick={onCancel}
                    ref={ref}
                    title="Close Popup"
                    data-testid="icon-close-button"
                    className="popup__close"
                    aria-label={closeIconHint}
                    role={'button'}
                    tabIndex={0}
                  >
                    {closeIcon ?? <CloseIcon aria-hidden />}
                  </IconButton>
                ) : null}
              </div>
            )}
            <div className="popup-title-box ">
              <div className="popup-heading">
                {headingIcon && <>{headingIcon}</>}
                {showPopupHeading ? (
                  <Typography
                    className="typo-text headline3_size heading-text "
                    component="div"
                    id={popUpHeadingId}
                    tabIndex={0}
                  >
                    {popupHeading}
                  </Typography>
                ) : null}
              </div>
              {popupTimer ? (
                <Typography
                  className="typo-text display3_size timer"
                  component="span"
                >
                  {popupTimer}
                </Typography>
              ) : (
                ''
              )}{' '}
            </div>
          </DialogTitle>

          <DialogContent>
            {contentType === 'element' ? (
              popupContent
            ) : contentType === 'text' && typeof popupContent === 'string' ? (
              <Typography
                id={popupContent}
                className={`typo-text body2_size content-text ${popupContentClass}`}
              >
                {popupContent}
              </Typography>
            ) : (
              ''
            )}
            {popupContentParagraphCheck ? (
              <div
                className={`typo-text body2_size content-text ${popupContentClass}`}
                dangerouslySetInnerHTML={{
                  __html: popupContentParagraph || '',
                }}
              />
            ) : null}
          </DialogContent>

          <div className={`${buttonStyle}`}>
            <DialogActions
              sx={{
                '& > :not(style) ~ :not(style)': {
                  marginLeft: '0px',
                },
              }}
            >
              {showCancelOption && (
                <AurButton
                  classNames="typo-text buttonLarge_size"
                  type="exit"
                  onClick={onCancel}
                  label={labelActionCancel}
                  title={cancelButtonTitle}
                  tabIndex={0}
                  dataTestId="popup-cancel-button"
                  ariaLabel={exitButtonHint}
                  ariaDescription={exitButtonAriaHintLabel}
                  disabled={actionCancelDisable}
                />
              )}
              {showOkOption && (
                <AurButton
                  classNames="typo-text buttonLarge_size pop-up-continue-button"
                  type="continue"
                  onClick={onOk}
                  label={labelActionOk}
                  tabIndex={0}
                  dataTestId="popup-confirm-button"
                  ariaLabel={continueButtonHint}
                  ariaDescription={continueButtonAriaHintLabel}
                  disabled={continueButtonState}
                  showLoader={actionOkLoader}
                  showLabelWithLoader={showActionOkWithLoader}
                  loaderClass={actionOkLoaderClass}
                />
              )}
            </DialogActions>
          </div>
        </Dialog>
      </>
    );
  }
);

export default AurPopup;
